import type { Router } from 'vue-router';

/**
 * Date Range Preservation Guard
 *
 * Automatically preserves `startDate` and `endDate` query parameters when
 * navigating between pages that opt-in via route meta fields.
 *
 * This ensures date range selections persist across page navigation
 * (e.g., Dashboard → Calls) without requiring manual query param management.
 *
 * Usage:
 * Add `meta: { preserveDateRange: true }` to pages that should share date state.
 *
 * Example in a page component:
 * ```vue
 * <route lang="yaml">
 * meta:
 *   preserveDateRange: true
 * </route>
 * ```
 *
 * Benefits:
 * - Opt-in design: Only pages with meta field participate
 * - URL remains source of truth (bookmarks, sharing work correctly)
 * - Browser back/forward work as expected
 * - No component changes needed
 *
 * @param router - Vue Router instance
 */
export function createDatePreservationGuard(router: Router): void {
  router.beforeEach((to, from, next) => {
    console.log('🔍 Date Preservation Guard:', {
      from: from.path,
      to: to.path,
      fromMeta: from.meta.preserveDateRange,
      toMeta: to.meta.preserveDateRange,
      fromQuery: from.query,
      toQuery: to.query,
    });

    // Only preserve dates if BOTH routes opt-in via meta field
    // This prevents unintended date param injection on pages that shouldn't have them
    const shouldPreserve = !!(to.meta.preserveDateRange && from.meta.preserveDateRange);

    console.log('📋 Should preserve?', shouldPreserve);

    if (!shouldPreserve) {
      console.log('❌ Not preserving - one or both routes missing meta field');
      return next();
    }

    // Check if destination route already has date params
    const hasDateInTo = to.query.startDate && to.query.endDate;

    // Check if source route has date params to preserve
    const hasDateInFrom = from.query.startDate && from.query.endDate;

    console.log('📅 Date params:', {
      hasDateInTo,
      hasDateInFrom,
      fromDates: { start: from.query.startDate, end: from.query.endDate },
    });

    // If navigating to a participating route that is missing date params,
    // and the route we are coming from has them, inject them
    if (!hasDateInTo && hasDateInFrom) {
      console.log('✅ Preserving date params from', from.path, 'to', to.path);
      return next({
        ...to,
        query: {
          ...to.query, // Preserve any existing query params (e.g., tab, filters)
          startDate: from.query.startDate,
          endDate: from.query.endDate,
        },
        // Use replace to avoid adding the intermediate URL (without params) to history
        // This ensures the back button works as expected
        replace: true,
      });
    }

    // No preservation needed - proceed normally
    console.log('⏭️ No preservation needed - proceeding normally');
    next();
  });
}
